import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Unidade de medida do insumo (`.spec/010`, módulo Compras).
 *
 * É o cadastro que diz **em que se conta** o insumo. Sem ele a quantidade é um
 * número solto: "12 de cimento" não distingue doze sacos de doze quilos, e o
 * custo médio dos dois difere por um fator de cinquenta.
 *
 * O cadastro **não movimenta nada**. Ele qualifica a quantidade — e, quando a
 * unidade é indivisível, é ele que autoriza o lançamento de estoque a recusar
 * uma fração.
 */

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Comprimento máximo da sigla.
 *
 * Vinte, e não três: é a sigla que a conta usa, não um código de tabela oficial.
 * "CX 12x1L" cabe, e é o tipo de coisa que aparece quando o fornecedor fatura em
 * embalagem fechada.
 */
export const TAMANHO_MAXIMO_SIGLA_UNIDADE = 20;

/**
 * A sigla aceita **um caractere**, ao contrário dos nomes dos outros cadastros.
 *
 * "M", "L" e "H" são siglas legítimas e das mais usadas. O mínimo de dois que
 * vale para nome de grupo ou de tipo de movimentação recusaria as três.
 */
const siglaSchema = z
  .string()
  .trim()
  .min(1, 'Informe a sigla da unidade.')
  .max(
    TAMANHO_MAXIMO_SIGLA_UNIDADE,
    `A sigla tem no máximo ${TAMANHO_MAXIMO_SIGLA_UNIDADE} caracteres.`,
  );

const nomeUnidadeSchema = z
  .string()
  .trim()
  .min(2, 'Informe o nome da unidade.')
  .max(200, 'O nome tem no máximo 200 caracteres.');

export const criarUnidadeMedidaSchema = z.object({
  sigla: siglaSchema,
  nome: nomeUnidadeSchema,
  /**
   * A unidade **não se parte**.
   *
   * Nasce desmarcada: a maioria das unidades de construção é divisível — metro,
   * quilo, litro —, e um padrão ligado faria a primeira entrada de 12,5 m de
   * cabo ser recusada sem que ninguém tivesse pedido isso.
   */
  apenasInteiros: z.boolean().prefault(false),
  ativo: z.boolean().prefault(true),
});

export type CriarUnidadeMedida = z.infer<typeof criarUnidadeMedidaSchema>;
export const atualizarUnidadeMedidaSchema = criarUnidadeMedidaSchema;
export type AtualizarUnidadeMedida = z.infer<typeof atualizarUnidadeMedidaSchema>;

export const unidadeMedidaSchema = z.object({
  id: uuidSchema,
  sigla: z.string(),
  nome: z.string(),
  apenasInteiros: z.boolean(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type UnidadeMedida = z.infer<typeof unidadeMedidaSchema>;

export const listaUnidadesMedidaSchema = respostaListaSchema(unidadeMedidaSchema);
export type ListaUnidadesMedida = z.infer<typeof listaUnidadesMedidaSchema>;

/**
 * Listagem, com um filtro além dos comuns.
 *
 * `apenasInteiros` porque a pergunta que se faz a esta tela depois de um
 * lançamento recusado é "quais unidades são indivisíveis?" — e ela não se
 * responde pelo campo de busca, que procura sigla e nome.
 */
export const listarUnidadesMedidaQuerySchema = listagemDeCadastroSchema.extend({
  apenasInteiros: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});

export type ListarUnidadesMedidaQuery = z.infer<typeof listarUnidadesMedidaQuerySchema>;

/**
 * Colunas de ordenação da listagem.
 *
 * A ordem padrão é a **sigla**: é por ela que a unidade é reconhecida na lista
 * suspensa do insumo, e é o que o usuário procura ao abrir a tela.
 */
export const COLUNAS_ORDENAVEIS_UNIDADE_MEDIDA = ['sigla', 'nome', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Quantidade inteira                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A quantidade cabe numa unidade indivisível?
 *
 * Mora no contrato, e não em cada tela, porque a mesma pergunta é feita em três
 * lugares — o formulário de lançamento, o contrato do lançamento e o service —,
 * e três implementações de "é inteiro" acabariam discordando em `10.0`.
 *
 * A verificação é **textual**, sobre a string decimal que trafega: `Number` em
 * quantidade de estoque é justamente o que a decisão de trafegar decimal como
 * texto evita. `"10"`, `"10.0"` e `"10.0000"` são todos inteiros; `"10.5"` não
 * é. Texto que não parece número devolve `true` — quem recusa formato é o
 * `quantidadeSchema`, e acusar as duas coisas na mesma mensagem confundiria.
 */
export function quantidadeCabeNaUnidade(quantidade: string, apenasInteiros: boolean): boolean {
  if (!apenasInteiros) return true;

  const [, decimais] = quantidade.trim().split('.');
  if (decimais === undefined) return true;

  return /^0*$/.test(decimais);
}
